import { useCallback, useEffect, useState } from "react";
import { Flame, Loader2, Sparkles, Check, X, Plus, Pencil, Trash2, Flag, Search, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DocsPanel } from "@/components/monitoring/DocsPanel";
import { LibreriaDocumenti } from "@/components/vesta/LibreriaDocumenti";

const PROB = { frequente: ["Frequente", "secondary"], non_risposta: ["Senza risposta", "destructive"], sbagliata: ["Da correggere", "destructive"] };
const dt = (s) => (s ? new Date(s).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "");

function KbDialog({ item, onClose, onSave }) {
  const [f, setF] = useState(item);
  const [busy, setBusy] = useState(false);
  useEffect(() => setF(item), [item]);
  if (!item) return null;
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => { setBusy(true); await onSave(f); setBusy(false); };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="kb-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>{item.titolo || "Risposta approvata"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input value={f.domanda || ""} onChange={set("domanda")} placeholder="Domanda tipo" data-testid="kb-domanda" />
          <Textarea rows={6} value={f.risposta || ""} onChange={set("risposta")} placeholder="Risposta corretta che Vesta dovrà dare" data-testid="kb-risposta" />
          <div className="grid grid-cols-2 gap-2">
            <Input value={f.argomento || ""} onChange={set("argomento")} placeholder="Argomento (montaggio, inverter…)" data-testid="kb-argomento" />
            <Input value={f.prodotto || ""} onChange={set("prodotto")} placeholder="Prodotto (facoltativo)" data-testid="kb-prodotto" />
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Annulla</Button><Button onClick={save} disabled={busy} data-testid="kb-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Approva e salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FontiPanel() {
  const [f, setF] = useState(null);
  const load = useCallback(() => api.get("/vesta-kb/fonti").then((r) => setF(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const sync = async () => { await api.post("/vesta-kb/fonti/sync"); toast.success("Aggiornamento avviato: sito e documenti vengono riletti in background"); setTimeout(load, 4000); };
  const s = f?.sito || {};
  const Row = ({ t, info, err, testid }) => (
    <div data-testid={testid}><div className="text-sm font-semibold">{t}</div><div className="text-xs text-muted-foreground">{info}</div>{err && <div className="text-xs text-destructive">{err}</div>}</div>
  );
  return (
    <Card className="flex flex-wrap items-center justify-between gap-4 p-4" data-testid="kb-fonti-panel">
      <Row t="Sito torri.solar" testid="kb-fonti-sito" err={s.error} info={s.last_ok ? `${s.pagine} pagine · ${s.blocchi} blocchi di testo · aggiornato ${dt(s.last_ok)}` : s.running ? "Lettura in corso…" : "Mai letto"} />
      <Button size="sm" variant="outline" onClick={sync} data-testid="kb-fonti-sync-btn">Aggiorna ora</Button>
    </Card>
  );
}

const Stat = ({ v, l, testid }) => <Card className="p-4" data-testid={testid}><div className="font-heading text-2xl font-bold">{v ?? "—"}</div><div className="text-xs text-muted-foreground">{l}</div></Card>;

export default function ConversazioniVesta() {
  const [st, setSt] = useState(null);
  const [prop, setProp] = useState([]);
  const [kb, setKb] = useState([]);
  const [conv, setConv] = useState([]);
  const [onlyFlag, setOnlyFlag] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => {
    api.get("/vesta-kb/stats").then((r) => setSt(r.data));
    api.get("/vesta-kb/proposte").then((r) => setProp(r.data.items));
    api.get("/vesta-kb").then((r) => setKb(r.data.items));
  }, []);
  const loadConv = useCallback(() => { api.get("/vesta-kb/conversazioni", { params: { segnalate: onlyFlag, q } }).then((r) => setConv(r.data.items)); }, [onlyFlag, q]);
  useEffect(load, [load]);
  useEffect(loadConv, [loadConv]);
  const analizza = async () => {
    setBusy(true);
    try { const r = await api.post("/vesta-kb/analisi", null, { timeout: 120000 }); toast.success(r.data.messaggio || `${r.data.nuove} nuove proposte da ${r.data.analizzate} conversazioni`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Analisi non riuscita"); }
    setBusy(false);
  };
  const save = async (f) => {
    try {
      if (f.mode === "proposta") await api.post(`/vesta-kb/proposte/${f.pid}/approva`, f);
      else if (f.id) await api.put(`/vesta-kb/${f.id}`, f);
      else await api.post("/vesta-kb", f);
      if (f.log_id) await api.post(`/vesta-kb/conversazioni/${f.log_id}/revisionata`);
      toast.success("Risposta approvata: Vesta la userà da subito"); setEdit(null); load(); loadConv();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const scarta = async (p) => { await api.post(`/vesta-kb/proposte/${p.id}/scarta`); load(); };
  const del = async (k) => { if (window.confirm("Eliminare questa risposta?")) { await api.delete(`/vesta-kb/${k.id}`); load(); } };
  return (
    <div className="space-y-6" data-testid="conversazioni-vesta-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-3xl font-bold tracking-tight"><Flame className="h-7 w-7 text-[#C17F59]" />Apprendimento Vesta</h1>
          <p className="text-sm text-muted-foreground">Le conversazioni vocali dei partner diventano risposte approvate che Vesta usa per prime.</p>
        </div>
        <Button onClick={analizza} disabled={busy} data-testid="kb-analizza-btn">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}Analizza nuove conversazioni{st?.da_analizzare ? ` (${st.da_analizzare})` : ""}</Button>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat v={st?.conversazioni} l="Conversazioni" testid="kb-stat-conv" /><Stat v={st?.segnalate} l="Segnalate dai partner" testid="kb-stat-flag" />
        <Stat v={st?.proposte} l="Proposte da rivedere" testid="kb-stat-prop" /><Stat v={st?.kb} l="Risposte approvate" testid="kb-stat-kb" /><Stat v={st?.usi_kb} l="Volte usate da Vesta" testid="kb-stat-usi" />
      </div>
      <DocsPanel />
      <FontiPanel />
      <Tabs defaultValue="proposte">
        <TabsList><TabsTrigger value="proposte" data-testid="kb-tab-proposte">Proposte ({prop.length})</TabsTrigger><TabsTrigger value="kb" data-testid="kb-tab-kb">Base di conoscenza ({kb.length})</TabsTrigger><TabsTrigger value="libreria" data-testid="kb-tab-libreria">Libreria documenti</TabsTrigger><TabsTrigger value="conv" data-testid="kb-tab-conv">Conversazioni</TabsTrigger></TabsList>
        <TabsContent value="proposte" className="space-y-3">
          {!prop.length && <p className="py-6 text-sm text-muted-foreground">Nessuna proposta: premi «Analizza nuove conversazioni».</p>}
          {prop.map((p) => (
            <Card key={p.id} className="p-0 overflow-hidden" data-testid={`kb-prop-${p.id}`}>
              <details className="group">
                <summary className="flex cursor-pointer list-none items-start gap-2 p-4 [&::-webkit-details-marker]:hidden">
                  <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2"><Badge variant={PROB[p.problema]?.[1]}>{PROB[p.problema]?.[0]}</Badge><Badge variant="outline">{p.argomento}</Badge><span className="text-xs text-muted-foreground">{p.occorrenze} conversazioni</span></div>
                    <div className="truncate font-semibold group-open:whitespace-normal">{p.domanda}</div>
                  </div>
                </summary>
                <div className="px-4 pb-4 pl-10">
                  <p className="text-sm text-muted-foreground">{p.risposta}</p>
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" onClick={() => setEdit({ ...p, mode: "proposta", pid: p.id, titolo: "Verifica e approva" })} data-testid={`kb-prop-approva-${p.id}`}><Check className="mr-1 h-4 w-4" />Verifica e approva</Button>
                    <Button size="sm" variant="outline" onClick={() => scarta(p)} data-testid={`kb-prop-scarta-${p.id}`}><X className="mr-1 h-4 w-4" />Scarta</Button>
                  </div>
                </div>
              </details>
            </Card>
          ))}
        </TabsContent>
        <TabsContent value="kb" className="space-y-3">
          <Button size="sm" variant="outline" onClick={() => setEdit({ titolo: "Nuova risposta" })} data-testid="kb-add-btn"><Plus className="mr-1 h-4 w-4" />Nuova risposta</Button>
          {kb.map((k) => (
            <Card key={k.id} className="p-4" data-testid={`kb-item-${k.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div><div className="font-semibold">{k.domanda}</div><p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{k.risposta}</p>
                  <div className="mt-2 text-[11px] text-muted-foreground">{k.argomento}{k.prodotto ? ` · ${k.prodotto}` : ""} · {k.autore} · {dt(k.updated_at)}</div></div>
                <div className="flex shrink-0 gap-1"><Button size="icon" variant="ghost" onClick={() => setEdit({ ...k, titolo: "Modifica risposta" })} data-testid={`kb-edit-${k.id}`}><Pencil className="h-4 w-4" /></Button><Button size="icon" variant="ghost" onClick={() => del(k)} data-testid={`kb-del-${k.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button></div>
              </div>
            </Card>
          ))}
        </TabsContent>
        <TabsContent value="libreria">
          <LibreriaDocumenti />
        </TabsContent>
        <TabsContent value="conv" className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-64"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca domanda o utente" className="pl-8" data-testid="kb-conv-search" /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={onlyFlag} onChange={(e) => setOnlyFlag(e.target.checked)} data-testid="kb-conv-flag-filter" />Solo segnalate</label>
          </div>
          {conv.map((c) => (
            <Card key={c.id} className="p-0 overflow-hidden" data-testid={`kb-conv-${c.id}`}>
              <details className="group">
                <summary className="flex cursor-pointer list-none items-start gap-2 p-4 [&::-webkit-details-marker]:hidden">
                  <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">{c.user_name} · {dt(c.at)}{c.segnalata && <Badge variant="destructive" className="gap-1"><Flag className="h-3 w-3" />Segnalata</Badge>}{c.revisione && <Badge variant="secondary">Rivista</Badge>}{(c.strumenti || []).map((s) => <Badge key={s} variant="outline">{s}</Badge>)}</div>
                    <div className="truncate text-sm group-open:whitespace-normal"><b>Partner:</b> {c.domanda}</div>
                  </div>
                </summary>
                <div className="px-4 pb-4 pl-10">
                  <div className="text-sm text-muted-foreground"><b>Vesta:</b> {c.risposta}</div>
                  {c.motivo_segnalazione && <div className="mt-1 text-xs text-destructive">{c.motivo_segnalazione}</div>}
                  <Button size="sm" variant="outline" className="mt-2" onClick={() => setEdit({ domanda: c.domanda, risposta: c.risposta, log_id: c.id, titolo: "Crea risposta approvata" })} data-testid={`kb-conv-crea-${c.id}`}><Check className="mr-1 h-4 w-4" />Crea risposta approvata</Button>
                </div>
              </details>
            </Card>
          ))}
          {!conv.length && <p className="py-6 text-sm text-muted-foreground">Nessuna conversazione.</p>}
        </TabsContent>
      </Tabs>
      <KbDialog item={edit} onClose={() => setEdit(null)} onSave={save} />
    </div>
  );
}
